import { Link } from 'react-router-dom';
import { FadeUp } from './Reveal';
import { NATIVE_BY_COUNTRY } from '../pages/NativeLanding';

/**
 * CountryFooter — footer variant for country microsite pages.
 * Omits the multi-market list from the primary footer, keeping the studio's
 * institutional identity and legal footer. Country-agnostic legal notices remain
 * identical to the shared Footer.
 */
export default function CountryFooter({ country }) {
  const native = NATIVE_BY_COUNTRY[country.code];
  return (
    <footer className="relative bg-ink-950 border-t border-white/10 mt-32" data-testid="country-footer">
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 py-24 md:py-32">
        <FadeUp>
          <div className="max-w-5xl">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/40 mb-6">
              — International practice · Let&apos;s talk in writing
            </p>
            <h2 className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight text-white font-light">
              An Italian lawyer <br />
              <em className="not-italic text-white/50">on your side in Italy.</em>
            </h2>
            <a
              href="#claim-form"
              data-testid="country-footer-cta"
              className="mt-10 inline-flex items-center gap-3 px-6 py-4 bg-white text-black text-[11px] font-mono uppercase tracking-[0.25em] hover:bg-oxblood hover:text-white transition-colors duration-300"
            >
              Submit Your Claim for Review
              <span aria-hidden>→</span>
            </a>
          </div>
        </FadeUp>

        <div className="mt-24 grid grid-cols-1 md:grid-cols-3 gap-10 pt-10 border-t border-white/10">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mb-3">Studio</p>
            <p className="font-serif text-lg text-white leading-snug">
              Studio Legale Belliardo
            </p>
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mb-3">Registered address</p>
            <p className="text-sm text-white/70 leading-relaxed">
              Salita Fontana del Ferro, 24 <br /> 37129 Verona · Italia
            </p>
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mb-3">International practice</p>
            <a
              href="mailto:avvocati@studiobelliardo.it"
              className="text-sm text-white hover-underline block"
              data-testid="country-footer-email"
            >
              avvocati@studiobelliardo.it
            </a>
            <span className="text-sm text-white/60 mt-1 block">Tel. +39 045.8011276</span>
            <span className="text-sm text-white/60 mt-1 block">PEC: avvchristianbelliardo@ordineavvocativrpec.it</span>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/10 grid grid-cols-1 md:grid-cols-2 gap-4 text-[11px] text-white/40 font-mono uppercase tracking-[0.2em]">
          <div>P.IVA 03995680232</div>
          <div className="md:text-right">Ordine Avvocati Verona N. 2726</div>
          <div className="md:col-span-2 text-white/40 mt-2">
            Professional Indemnity Insurance No. M11635296 · ITAS Mutua · renewed 30/01/2026
          </div>
          <div className="md:col-span-2 mt-4 flex gap-8 text-white/40">
            <Link to="/privacy" data-testid="country-footer-privacy-link" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/cookie-policy" data-testid="country-footer-cookie-link" className="hover:text-white transition-colors">Cookie Policy</Link>
            <Link to="/for-lawyers" data-testid="country-footer-counsel-link" className="hover:text-white transition-colors">For Counsel</Link>
          </div>
          {native && (
            <div className="md:col-span-2 mt-4 text-white/40" data-testid="country-footer-native-note">
              <Link to={native.path} data-testid="country-footer-native-link" className="hover:text-white transition-colors">
                {native.linkLabel}
              </Link>
            </div>
          )}
          <div className="md:col-span-2 text-white/30 mt-4 leading-relaxed normal-case tracking-normal font-sans">
            All content on this website is provided for informational purposes only and does not constitute legal
            advice or a solicitation. The information does not create an attorney–client relationship. Any reference
            to prior representations is illustrative and does not imply guarantee of outcome — as per Italian
            deontological rules (Codice Deontologico Forense, art. 35, 17 &amp; 12).
          </div>
        </div>
      </div>
    </footer>
  );
}
